import { useCallback, useEffect, useRef, useState } from 'react';
import { billingApi } from '../../services/billingApi';
import type { BillingSource, ClaimSummaryDto } from '../../types/billing';

/** Statuses shown on Triage by default - the "operator needs to act" subset.
 *  Excludes DISPATCHED (already in flight to BETHY), OA_DRAFT_CREATED /
 *  SUBMITTED / MANUALLY_COMPLETED (already terminal). */
const ACTIONABLE_STATUSES = ['STAGED', 'FAILED', 'NEEDS_REVIEW', 'SKIPPED'];

/** One-shot page size. Triage doesn't paginate today (operators triage a few
 *  hundred actionable claims; the dispatcher and Claims page handle the long
 *  tail). Bump this if a single practice routinely sits on more than this many
 *  actionable claims. */
const TRIAGE_PAGE_LIMIT = 200;

export interface UseTriageDataResult {
  claims: ClaimSummaryDto[];
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

export function useTriageData(
  practiceId: string | undefined,
  source: BillingSource | undefined,
): UseTriageDataResult {
  const [claims, setClaims] = useState<ClaimSummaryDto[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState<number>(0);

  const isMountedRef = useRef<boolean>(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const reload = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!practiceId) {
      setClaims([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    billingApi
      .listPracticeClaims(practiceId, {
        status: ACTIONABLE_STATUSES,
        source,
        limit: TRIAGE_PAGE_LIMIT,
      })
      .then((page) => {
        if (cancelled || !isMountedRef.current) return;
        setClaims(page.items);
        setError(null);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled || !isMountedRef.current) return;
        const message =
          err instanceof Error && err.message ? err.message : 'Failed to load claims';
        setClaims([]);
        setError(message);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [practiceId, source, reloadKey]);

  return { claims, isLoading, error, reload };
}
